import { useLayoutEffect, useRef, type InputHTMLAttributes } from "react";
import { mascaraDeDocumento } from "@/formato";
import { Campo } from "./Campo";

/*
 * O campo de CPF ou CNPJ, com máscara de digitação.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A MÁSCARA PONTUA, E NÃO VALIDA.                                          │
 * │                                                                          │
 * │ Quem decide o que é documento é `domain.VerificarDocumento`, e o valor   │
 * │ vai à API como está na tela, com a pontuação, que ela descarta. Conferir │
 * │ dígito aqui seria uma segunda regra (emenda ao doc 06a §5).              │
 * │                                                                          │
 * │ `inputMode="numeric"` é PROIBIDO: o CNPJ alfanumérico tem letras, e o    │
 * │ teclado numérico do celular não as digita.                               │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
interface Props extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "id" | "value" | "onChange" | "inputMode"
> {
  rotulo?: string;
  valor: string;
  /** Recebe o valor pontuado. */
  aoMudar: (valor: string) => void;
  dica?: string | undefined;
  erro?: string | undefined;
}

const SIGNIFICATIVO = /[0-9a-zA-Z]/;

export function CampoDeDocumento({ rotulo = "CNPJ ou CPF", valor, aoMudar, ...resto }: Props) {
  const campo = useRef<HTMLInputElement>(null);

  /*
   * A máscara reescreve o valor, e o navegador manda o cursor para o fim. Corrigir um
   * dígito no meio viraria redigitar tudo dali para a frente. A posição é contada em
   * caracteres significativos, porque os separadores mudam de lugar.
   */
  const significativosAntes = useRef<number | null>(null);

  useLayoutEffect(() => {
    const alvo = significativosAntes.current;
    if (alvo === null || campo.current === null) return;
    significativosAntes.current = null;

    const texto = campo.current.value;
    let posicao = 0;
    for (let vistos = 0; posicao < texto.length && vistos < alvo; posicao += 1) {
      if (SIGNIFICATIVO.test(texto.charAt(posicao))) vistos += 1;
    }
    campo.current.setSelectionRange(posicao, posicao);
  });

  return (
    <Campo
      ref={campo}
      rotulo={rotulo}
      value={valor}
      onChange={(evento) => {
        const { value, selectionStart } = evento.target;
        significativosAntes.current = [...value.slice(0, selectionStart ?? value.length)].filter(
          (caractere) => SIGNIFICATIVO.test(caractere),
        ).length;
        aoMudar(mascaraDeDocumento(value));
      }}
      autoComplete="off"
      spellCheck={false}
      className="dados"
      {...resto}
    />
  );
}
